import {useCallback, useMemo, useState} from "react";
import PropTypes from "prop-types";
import {sendContact} from "../api/contactApi";
import {SITE_CONFIG} from "../config/siteConfig";
import {useT} from "../i18n/i18n";
import SocialIconButton from "./SocialIconButton";

const INITIAL_FORM_STATE = {
    name: "",
    email: "",
    message: "",
    website: "", // honeypot
};

export default function Contacts({language}) {
    const {t, tObject} = useT(language);
    const fields = tObject("contacts.fields");

    const [form, setForm] = useState(INITIAL_FORM_STATE);
    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState(null);
    const [errorMessage, setErrorMessage] = useState("");

    const contactLinks = useMemo(
        () => [
            {key: "instagram", label: "Instagram", href: SITE_CONFIG.instagram, icon: "instagram"},
            {key: "linkedin", label: "LinkedIn", href: SITE_CONFIG.linkedin, icon: "linkedin"},
            {key: "github", label: "GitHub", href: SITE_CONFIG.github, icon: "github"},
            {key: "email", label: SITE_CONFIG.email, href: `mailto:${SITE_CONFIG.email}`, icon: "email"},
            // {key: "phone", label: SITE_CONFIG.phone, icon: "phone"}, // unclickable
        ],
        []
    );

    const handleChange = useCallback((e) => {
        const {name, value} = e.target;
        setForm((prev) => ({...prev, [name]: value}));
    }, []);

    const handleSubmit = useCallback(async (e) => {
        e.preventDefault();
        if (form.website) return;

        setLoading(true);
        setStatus(null);
        setErrorMessage("");

        try {
            await sendContact(form);
            setStatus("success");
            setForm(INITIAL_FORM_STATE);
        } catch (err) {
            setStatus("error");
            setErrorMessage(err?.message || t("contacts.error"));
        } finally {
            setLoading(false);
        }
    }, [form, t]);

    return (
        <section className="py-16 px-4 sm:px-6 max-w-3xl mx-auto">
            {/* CTA Header */}
            <div className="text-center mb-12 sm:mb-16">
                <p className="text-[12px] font-bold uppercase tracking-widest text-[rgb(var(--app-accent))] mb-3">
                    {language === "en" ? "READY TO START?" : "PRONTI PER INIZIARE?"}
                </p>
                <h2 className="text-3xl sm:text-4xl font-extrabold mb-4 text-black leading-tight">
                    {t("contacts.title")}
                </h2>
                <p className="text-[15px] sm:text-[16px] text-black/70 leading-relaxed max-w-xl mx-auto">
                    {t("contacts.description")}
                </p>
            </div>

            {/* Social Links */}
            <ContactLinks 
                links={contactLinks} 
                directText={language === "en" ? "Connect With Me" : "Connettiti Con Me"}
            />

            <form onSubmit={handleSubmit} className="mt-12 space-y-6 bg-gray-50 p-8 sm:p-10 rounded-2xl border border-gray-200 shadow-sm" noValidate>
                <InputField id="name" name="name" value={form.name} onChange={handleChange}
                    placeholder={fields.name || ""}/>
                <InputField id="email" name="email" type="email" value={form.email} onChange={handleChange}
                    placeholder={fields.email || ""}/>
                <TextAreaField id="message" name="message" value={form.message} onChange={handleChange}
                    placeholder={fields.message || ""}/>

                <input name="website" value={form.website} onChange={handleChange} className="hidden"
                    autoComplete="off"
                    tabIndex={-1}/>

                <button type="submit"
                    disabled={loading}
                    className="w-full py-3 rounded-lg font-semibold bg-black text-white hover:bg-black/90 disabled:opacity-50 transition-colors">
                    {loading ? t("contacts.sending") : t("contacts.send")}
                </button>

                {status === "success" && (
                    <p className="font-medium mt-2 text-green-600" role="status" aria-live="polite">{t("contacts.success")}</p>
                )}
                {status === "error" && (
                    <p className="font-medium mt-2 text-red-600" role="alert">{errorMessage}</p>
                )}
            </form>
        </section>
    );
}

Contacts.propTypes = {
    language: PropTypes.oneOf(["en", "it"]),
};

function ContactLinks({links, directText}) {
    return (
        <div className="mb-12 text-center">
            <p className="text-sm font-semibold uppercase tracking-widest text-black/60 mb-6">{directText}</p>
            <ul className="flex flex-wrap gap-4 sm:gap-5 justify-center items-center">
                {links.map(({key, label, href, icon}) => (
                    <li key={key}>
                        {href ? (
                            <SocialIconButton href={href} label={label} icon={icon} />
                        ) : (
                            <span
                                className="flex items-center justify-center w-14 h-14 border border-black/15 rounded-xl bg-black/5 cursor-default text-black transition-all duration-300"
                                aria-label={label}
                                title={label}>
                                <img src={`/icons/${icon}.png`} alt={`${label} icon`} className="w-6 h-6"/>
                            </span>
                        )}
                    </li>
                ))}
            </ul>
        </div>
    );
}

ContactLinks.propTypes = {
    links: PropTypes.arrayOf(
        PropTypes.shape({
            key: PropTypes.string.isRequired,
            label: PropTypes.string.isRequired,
            href: PropTypes.string,
            icon: PropTypes.string.isRequired,
        })
    ).isRequired,
    directText: PropTypes.string.isRequired,
};

function InputField({id, name, type = "text", value, onChange, placeholder}) {
    return (
        <>
            <label htmlFor={id} className="sr-only">
                {placeholder}
            </label>
            <input id={id} name={name} type={type} required value={value} onChange={onChange} placeholder={placeholder}
                className="w-full p-4 border border-gray-300 rounded-lg bg-white text-black placeholder-gray-500 transition focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent hover:border-gray-400"
            />
        </>
    );
}

InputField.propTypes = {
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    type: PropTypes.string,
    value: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
    placeholder: PropTypes.string.isRequired,
};

function TextAreaField({id, name, value, onChange, placeholder}) {
    return (
        <>
            <label htmlFor={id} className="sr-only">
                {placeholder}
            </label>
            <textarea id={id} name={name} rows={5} required value={value} onChange={onChange} placeholder={placeholder}
                className="w-full p-4 border border-gray-300 rounded-lg bg-white text-black placeholder-gray-500 transition focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent hover:border-gray-400 resize-none"/>
        </>
    );
}

TextAreaField.propTypes = {
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
    placeholder: PropTypes.string.isRequired,
};
